import { useCallback, useEffect, useRef, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { loadCurrentUnionRoster } from '../../union-roster/api/unionRosterApi.js';
import {
  createRaidPracticeAdmin,
  deleteRaidPracticeAdmin,
  loadRaidPracticeAdmin,
  updateRaidPracticeAdmin,
} from '../api/raidPracticeAdminApi.js';
import { deleteRaidPractice, saveRaidPractice } from '../api/raidPracticeApi.js';
import { buildRaidPracticeAdminMembers } from '../domain/raidPracticeAdminMembers.js';
import { useRaidPracticeRecommendationCatalog } from './useRaidPracticeRecommendationCatalog.js';

const EMPTY = { unionId: '', unionName: '', viewerRole: '', viewerMemberOpenid: '', seasons: [], selectedSeason: null };
const MANAGER_ROLES = new Set(['leader', 'deputy']);

function memberOpenid(value) {
  const source = value && typeof value === 'object' ? value : {};
  return String(source.openid || source.memberOpenid || source.member_openid || '').trim();
}

function sameMember(left, right) {
  const leftOpenid = memberOpenid(left);
  const rightOpenid = memberOpenid(right);
  return Boolean(leftOpenid && rightOpenid && leftOpenid === rightOpenid);
}

function applyRosterMembers(practiceData, rosterData) {
  const selectedSeason = practiceData?.selectedSeason || null;
  if (!selectedSeason) return practiceData;

  const viewerMemberOpenid = String(practiceData?.viewerMemberOpenid || '').trim();
  const members = buildRaidPracticeAdminMembers({
    rosterMembers: rosterData?.members || [],
    fallbackMembers: selectedSeason.members || [],
    practiceRecords: selectedSeason.practiceRecords || [],
    actualRecords: selectedSeason.actualRecords || [],
  }).map((member) => ({
    ...member,
    isSelf: Boolean(member?.isSelf || (viewerMemberOpenid && memberOpenid(member) === viewerMemberOpenid)),
  }));

  return {
    ...practiceData,
    selectedSeason: {
      ...selectedSeason,
      members,
    },
  };
}

function selfMember(data) {
  return (Array.isArray(data?.selectedSeason?.members) ? data.selectedSeason.members : [])
    .find((member) => Boolean(member?.isSelf)) || null;
}

function practiceRecord(data, recordId) {
  return (Array.isArray(data?.selectedSeason?.practiceRecords) ? data.selectedSeason.practiceRecords : [])
    .find((record) => String(record?.id || '') === String(recordId || '')) || null;
}

function notifyPersonalPracticeChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('dororaid:raid-overview-changed', {
    detail: { source: 'raid-practice-admin' },
  }));
}

export function useRaidPracticeAdmin() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });
  const runRef = useRef(0);
  const selectedSeasonNoRef = useRef(null);
  const requestRef = useRef({ key: '', promise: null });
  const blockingRequestRef = useRef(null);
  const {
    loading: catalogLoading,
    errorMessage: catalogErrorMessage,
  } = useRaidPracticeRecommendationCatalog();

  const refresh = useCallback(async (seasonNo = null, { silent = false } = {}) => {
    const requestedSeasonNo = seasonNo == null ? null : Number(seasonNo);
    const normalizedSeasonNo = Number.isFinite(requestedSeasonNo) ? requestedSeasonNo : null;
    const requestKey = normalizedSeasonNo == null ? 'current' : String(normalizedSeasonNo);
    const runId = runRef.current + 1;
    runRef.current = runId;

    let requestPromise = requestRef.current.key === requestKey
      ? requestRef.current.promise
      : null;
    if (!requestPromise) {
      requestPromise = Promise.all([
        loadRaidPracticeAdmin(normalizedSeasonNo),
        loadCurrentUnionRoster().catch(() => null),
      ]).then(([practiceData, rosterData]) => applyRosterMembers(practiceData, rosterData));
      requestRef.current = { key: requestKey, promise: requestPromise };
    }

    if (!silent) {
      blockingRequestRef.current = requestPromise;
      setLoading(true);
    } else if (blockingRequestRef.current) {
      blockingRequestRef.current = requestPromise;
    }

    try {
      const next = await requestPromise;
      if (runRef.current !== runId) return null;
      selectedSeasonNoRef.current = next?.selectedSeason?.seasonNo || null;
      setData(next);
      setErrorMessage('');
      return next;
    } catch (error) {
      if (runRef.current === runId) setErrorMessage(error instanceof Error ? error.message : '전체 멤버 모의전을 불러오지 못했습니다.');
      return null;
    } finally {
      if (requestRef.current.promise === requestPromise) requestRef.current = { key: '', promise: null };
      if (blockingRequestRef.current === requestPromise) {
        blockingRequestRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refresh();
    const changed = (event) => {
      if (event?.detail?.source === 'raid-practice-admin') return;
      refresh(selectedSeasonNoRef.current, { silent: true });
    };
    window.addEventListener('dororaid:raid-overview-changed', changed);
    window.addEventListener('dororaid:union-roster-changed', changed);
    return () => {
      runRef.current += 1;
      window.removeEventListener('dororaid:raid-overview-changed', changed);
      window.removeEventListener('dororaid:union-roster-changed', changed);
    };
  }, [refresh]);

  const selectSeason = useCallback((seasonNo) => {
    const normalized = Number(seasonNo);
    if (!Number.isInteger(normalized)) return;
    refresh(normalized);
  }, [refresh]);

  const create = useCallback(async ({ member, boss, payload }) => {
    if (working || !member || !boss || !data.selectedSeason) return false;
    const canManageAll = MANAGER_ROLES.has(String(data.viewerRole || ''));
    const canManageTarget = canManageAll || Boolean(member?.isSelf) || sameMember(member, selfMember(data));
    if (!canManageTarget) {
      setNotice({ type: 'error', message: '일반 멤버는 자신의 모의전 기록만 추가할 수 있습니다.' });
      return false;
    }

    setWorking(`save:${payload.slotNo}`);
    setNotice({ type: '', message: '' });
    try {
      if (canManageAll) {
        await createRaidPracticeAdmin({
          unionId: data.unionId,
          seasonId: data.selectedSeason.id,
          seasonNo: data.selectedSeason.seasonNo,
          memberOpenid: memberOpenid(member),
          memberNickname: member.nickname,
          bossNo: boss.bossNo,
          payload,
        });
      } else {
        await saveRaidPractice({
          ...payload,
          seasonNo: data.selectedSeason.seasonNo,
          bossNo: boss.bossNo,
        });
      }
      await refresh(data.selectedSeason.seasonNo, { silent: true });
      notifyPersonalPracticeChanged();
      setNotice({ type: 'success', message: `${member.nickname || '멤버'}의 ${bossDisplayName(boss)} ${payload.slotNo}번 슬롯을 추가했습니다.` });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '모의전 기록을 추가하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [data, refresh, working]);

  const update = useCallback(async (recordId, payload) => {
    if (working || !recordId || !data.selectedSeason) return false;
    const targetRecord = practiceRecord(data, recordId);
    const canManageAll = MANAGER_ROLES.has(String(data.viewerRole || ''));
    const canManageTarget = canManageAll || sameMember(targetRecord, selfMember(data));
    if (!targetRecord || !canManageTarget) {
      setNotice({ type: 'error', message: '일반 멤버는 자신의 모의전 기록만 수정할 수 있습니다.' });
      return false;
    }

    setWorking(`save:${payload.slotNo}`);
    setNotice({ type: '', message: '' });
    try {
      if (canManageAll) {
        await updateRaidPracticeAdmin({ unionId: data.unionId, recordId, payload });
      } else {
        await saveRaidPractice({
          ...payload,
          seasonNo: data.selectedSeason.seasonNo,
          bossNo: targetRecord.bossNo,
        });
      }
      await refresh(data.selectedSeason.seasonNo, { silent: true });
      notifyPersonalPracticeChanged();
      setNotice({ type: 'success', message: `${payload.slotNo}번 모의전 슬롯을 수정했습니다.` });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '모의전 기록을 수정하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [data, refresh, working]);

  const remove = useCallback(async (recordId, slotNo) => {
    if (working || !recordId || !data.selectedSeason) return false;
    const targetRecord = practiceRecord(data, recordId);
    const canManageAll = MANAGER_ROLES.has(String(data.viewerRole || ''));
    const canManageTarget = canManageAll || sameMember(targetRecord, selfMember(data));
    if (!targetRecord || !canManageTarget) {
      setNotice({ type: 'error', message: '일반 멤버는 자신의 모의전 기록만 삭제할 수 있습니다.' });
      return false;
    }

    setWorking(`delete:${slotNo}`);
    setNotice({ type: '', message: '' });
    try {
      if (canManageAll) await deleteRaidPracticeAdmin({ unionId: data.unionId, recordId });
      else await deleteRaidPractice(recordId);
      await refresh(data.selectedSeason.seasonNo, { silent: true });
      notifyPersonalPracticeChanged();
      setNotice({ type: 'success', message: `${slotNo}번 모의전 슬롯을 삭제했습니다.` });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '모의전 기록을 삭제하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [data, refresh, working]);

  return {
    ...data,
    loading: loading || catalogLoading,
    working,
    errorMessage: errorMessage || catalogErrorMessage,
    notice,
    refresh,
    selectSeason,
    create,
    update,
    remove,
  };
}
